import React from 'react';
import { Eye, Info } from 'lucide-react';

/**
 * Компонент для визуализации комнаты с обоями
 */
const RoomVisualization = ({ roomData, rollsCount, wallpaperType }) => {
  // Определяем цвет обоев в зависимости от типа
  const getWallpaperColor = (type) => {
    const colors = {
      'standard': '#f0e6d2', // бежевый для стандартных
      'wide': '#e6f0f0',     // светло-голубой для широких
      'vinyl': '#e6f5e6',    // светло-зеленый для виниловых
      'nonwoven': '#f2e6f0', // светло-фиолетовый для флизелиновых
      'textile': '#f5f5e6',  // светло-желтый для текстильных
      'photoWall': '#e6e6f5', // светло-синий для фотообоев
      'liquid': '#f0f0e6'     // светло-серый для жидких обоев
    };
    
    return colors[type] || '#f2f2f2'; // светло-серый по умолчанию
  };
  
  // Определяем текстуру обоев в зависимости от типа
  const getWallpaperPattern = (type) => {
    // SVG паттерны для разных типов обоев
    const patterns = {
      'standard': `
        <pattern id="standard-pattern" patternUnits="userSpaceOnUse" width="20" height="20" patternTransform="rotate(0)">
          <rect width="20" height="20" fill="${getWallpaperColor(type)}" />
          <path d="M 0,10 l 20,0" stroke="#00000015" stroke-width="0.5" />
        </pattern>
      `,
      'wide': `
        <pattern id="wide-pattern" patternUnits="userSpaceOnUse" width="30" height="30" patternTransform="rotate(0)">
          <rect width="30" height="30" fill="${getWallpaperColor(type)}" />
          <path d="M 15,0 l 0,30" stroke="#00000010" stroke-width="0.5" />
        </pattern>
      `,
      'vinyl': `
        <pattern id="vinyl-pattern" patternUnits="userSpaceOnUse" width="20" height="20">
          <rect width="20" height="20" fill="${getWallpaperColor(type)}" />
          <circle cx="10" cy="10" r="3" fill="#00000008" />
        </pattern>
      `,
      'nonwoven': `
        <pattern id="nonwoven-pattern" patternUnits="userSpaceOnUse" width="30" height="30">
          <rect width="30" height="30" fill="${getWallpaperColor(type)}" />
          <path d="M 0,0 l 30,30 M 30,0 l -30,30" stroke="#00000010" stroke-width="0.5" />
        </pattern>
      `,
      'textile': `
        <pattern id="textile-pattern" patternUnits="userSpaceOnUse" width="10" height="10">
          <rect width="10" height="10" fill="${getWallpaperColor(type)}" />
          <path d="M 0,0 h 10 v 10 h -10 z" stroke="#00000015" stroke-width="0.5" fill="none" />
        </pattern>
      `,
      'photoWall': `
        <pattern id="photowall-pattern" patternUnits="userSpaceOnUse" width="40" height="40">
          <rect width="40" height="40" fill="${getWallpaperColor(type)}" />
          <rect x="5" y="5" width="30" height="30" fill="#00000008" rx="2" />
        </pattern>
      `,
      'liquid': `
        <pattern id="liquid-pattern" patternUnits="userSpaceOnUse" width="20" height="20">
          <rect width="20" height="20" fill="${getWallpaperColor(type)}" />
          <circle cx="5" cy="5" r="1" fill="#00000010" />
          <circle cx="15" cy="15" r="1" fill="#00000010" />
          <circle cx="5" cy="15" r="1" fill="#00000010" />
          <circle cx="15" cy="5" r="1" fill="#00000010" />
        </pattern>
      `
    };
    
    return patterns[type] || patterns['standard'];
  };
  
  // Масштаб SVG для соответствия размерам комнаты
  const scaleFactor = 30; // 1 метр = 30 пикселей
  
  // Получаем размеры комнаты в пикселях
  const roomWidth = roomData.width * scaleFactor;
  const roomLength = roomData.length * scaleFactor;
  const roomHeight = roomData.height * scaleFactor;
  
  // Расположение и размеры окон и дверей
  // Упрощенно распределяем их по стенам
  const windows = roomData.windows || [];
  const doors = roomData.doors || [];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Eye className="w-5 h-5 mr-2 text-indigo-500" />
          Визуализация комнаты
        </h3>
      </div>
      
      {/* Информационное сообщение */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>
            Схематичное изображение комнаты с нанесенными обоями. 
            Для {rollsCount > 1 ? `расчета потребовалось ${rollsCount} ${rollsCount < 5 ? 'рулона' : 'рулонов'}` : 'расчета потребовался 1 рулон'} обоев.
          </p>
        </div>
      </div>
      
      {/* Визуализация комнаты в SVG */}
      <div className="overflow-auto max-w-full bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
        <svg 
          viewBox={`0 0 ${roomLength + 20} ${roomWidth + roomHeight + 40}`} 
          className="w-full h-auto max-h-[500px]"
          style={{ minHeight: '300px' }}
        >
          <defs>
            {/* Паттерны для обоев */}
            {getWallpaperPattern(wallpaperType)}
            
            {/* Паттерн для пола */}
            <pattern id="floor-pattern" patternUnits="userSpaceOnUse" width="30" height="30" patternTransform="rotate(45)">
              <rect width="30" height="30" fill="#d4c4a8" />
              <rect x="0" y="0" width="15" height="15" fill="#c4b698" />
              <rect x="15" y="15" width="15" height="15" fill="#c4b698" />
            </pattern>
            
            {/* Паттерн для потолка */}
            <pattern id="ceiling-pattern" patternUnits="userSpaceOnUse" width="20" height="20">
              <rect width="20" height="20" fill="#f8f8f8" />
              <path d="M 10,0 l 0,20 M 0,10 l 20,0" stroke="#00000008" stroke-width="0.5" />
            </pattern>
          </defs>
          
          {/* Пол */}
          <rect 
            x="10" 
            y={roomHeight + 20} 
            width={roomLength} 
            height={roomWidth} 
            fill="url(#floor-pattern)" 
            stroke="#a0a0a0" 
            strokeWidth="1" 
          />
          
          {/* Задняя стена */}
          <rect 
            x="10" 
            y="10" 
            width={roomLength} 
            height={roomHeight} 
            fill={`url(#${wallpaperType}-pattern)`} 
            stroke="#a0a0a0" 
            strokeWidth="1" 
          />
          
          {/* Левая стена (боковая) */}
          <polygon 
            points={`10,10 10,${roomHeight + 10} 10,${roomHeight + roomWidth + 10} 10,${roomWidth + 10}`}
            fill={`url(#${wallpaperType}-pattern)`} 
            stroke="#a0a0a0" 
            strokeWidth="1" 
          />
          
          {/* Потолок */}
          <rect 
            x="10" 
            y="10" 
            width={roomLength} 
            height="0" 
            fill="url(#ceiling-pattern)" 
            stroke="#a0a0a0" 
            strokeWidth="1" 
          />
          
          {/* Окна на задней стене */}
          {windows.map((window, index) => {
            // Распределяем окна на задней стене
            const x = 10 + roomLength / 2 - (window.width * scaleFactor) / 2;
            const y = 10 + roomHeight / 2 - (window.height * scaleFactor) / 2;
            
            return (
              <rect 
                key={`window-${index}`}
                x={x} 
                y={y} 
                width={window.width * scaleFactor} 
                height={window.height * scaleFactor} 
                fill="#e6f7ff" 
                stroke="#4a9eff" 
                strokeWidth="2" 
              />
            );
          })}
          
          {/* Двери на левой стене */}
          {doors.map((door, index) => {
            // Размещаем дверь на левой стене
            const x = 10;
            const y = 10 + roomHeight - (door.height * scaleFactor);
            
            return (
              <rect 
                key={`door-${index}`}
                x={x} 
                y={y} 
                width={2} // толщина двери небольшая, т.к. она видна сбоку
                height={door.height * scaleFactor} 
                fill="#8B4513" 
                stroke="#5D2906" 
                strokeWidth="1" 
              />
            );
          })}
          
          {/* Текст с размерами */}
          <text 
            x={10 + roomLength / 2} 
            y={roomHeight + roomWidth + 30} 
            textAnchor="middle" 
            fill="currentColor" 
            className="text-gray-700 dark:text-gray-300"
            fontSize="12"
          >
            {roomData.length} м
          </text>
          
          <text 
            x={roomLength + 25} 
            y={10 + roomHeight / 2} 
            textAnchor="middle" 
            fill="currentColor" 
            className="text-gray-700 dark:text-gray-300"
            fontSize="12"
            transform={`rotate(90 ${roomLength + 25} ${10 + roomHeight / 2})`}
          >
            {roomData.height} м
          </text>
          
          <text 
            x={roomLength + 25} 
            y={roomHeight + 10 + roomWidth / 2} 
            textAnchor="middle" 
            fill="currentColor" 
            className="text-gray-700 dark:text-gray-300"
            fontSize="12"
            transform={`rotate(90 ${roomLength + 25} ${roomHeight + 10 + roomWidth / 2})`}
          >
            {roomData.width} м
          </text>
        </svg>
      </div>
      
      {/* Пояснение условных обозначений */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <div className="flex items-center">
          <div className={`w-4 h-4 mr-2 rounded-sm`} style={{ background: getWallpaperColor(wallpaperType) }}></div>
          <span className="text-gray-700 dark:text-gray-300">Обои</span>
        </div>
        
        {windows.length > 0 && (
          <div className="flex items-center">
            <div className="w-4 h-4 mr-2 rounded-sm bg-blue-200 border border-blue-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Окна</span>
          </div>
        )}
        
        {doors.length > 0 && (
          <div className="flex items-center">
            <div className="w-4 h-4 mr-2 rounded-sm bg-amber-800"></div>
            <span className="text-gray-700 dark:text-gray-300">Двери</span>
          </div>
        )}
        
        <div className="flex items-center">
          <div className="w-4 h-4 mr-2 rounded-sm bg-amber-200"></div>
          <span className="text-gray-700 dark:text-gray-300">Пол</span>
        </div>
      </div>
    </div>
  );
};

export default RoomVisualization;